import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

interface ConfirmationOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  tone?: 'default' | 'danger';
}

type Confirm = (options: ConfirmationOptions) => Promise<boolean>;

const ConfirmationContext = createContext<Confirm | null>(null);

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<(ConfirmationOptions & { resolve: (answer: boolean) => void }) | null>(null);
  const confirm = useCallback<Confirm>((options) => new Promise((resolve) => setPending({ ...options, resolve })), []);

  function finish(answer: boolean) {
    pending?.resolve(answer);
    setPending(null);
  }

  return (
    <ConfirmationContext.Provider value={confirm}>
      {children}
      {pending && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && finish(false)}>
          <section className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-message">
            <h2 id="confirm-title">{pending.title}</h2>
            <p id="confirm-message">{pending.message}</p>
            <div className="dialog-actions">
              <button className="secondary-button" onClick={() => finish(false)}>Cancel</button>
              <button className={pending.tone === 'danger' ? 'danger-button' : 'primary-button'} onClick={() => finish(true)} autoFocus>{pending.confirmLabel || 'Confirm'}</button>
            </div>
          </section>
        </div>
      )}
    </ConfirmationContext.Provider>
  );
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) throw new Error('useConfirmation must be used inside ConfirmationProvider');
  return context;
}
